import React from 'react';
import { Flex, Card, Text } from './base-components';

interface Props {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  /** Style the confirm button as a destructive action. */
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

// Lightweight on-brand replacement for window.confirm(), matching the styling of
// the other modals (dark card on a dimmed backdrop, click-outside to dismiss).
export const ConfirmDialog: React.FC<Props> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busy = false,
  destructive = false,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;

  const confirmClasses = destructive
    ? 'text-white bg-red-500/90 hover:bg-red-500'
    : 'text-white bg-[#3b82f6] hover:bg-[#2563eb]';

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      zIndex={1000}
      onClick={(e) => {
        if (!busy && e.target === e.currentTarget) onClose();
      }}
    >
      <Card
        fullWidth
        maxWidth="sm"
        shadow
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="heading" mb={2}>{title}</Text>
        <div className="text-sm text-white/70 mb-5 leading-relaxed">{message}</div>
        <Flex justify="end" gap="0.75rem">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 border border-white/15 bg-white/5 hover:bg-white/10 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${confirmClasses}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </Flex>
      </Card>
    </Flex>
  );
};

export default ConfirmDialog;
